/**
 * 학생 학교·학년 첫설정과 내 자료 고르기(PR36 beta). app.ts는 이 모듈을 열기만 한다.
 * 화면은 권한을 판단하지 않고 서버 응답만 표시한다. 조회 실패·프로필 미등록·빈 목록을 서로 다른 문구로 구분하고,
 * 기다리는 사이 계정·화면이 바뀌면(live) 그 응답은 버린다. 제공은 학생 배포 허용이 아니며 서버가 정한다.
 */
import { h, clear, toast } from './dom.js';
import { currentGen, type Transport } from '../core/transport.js';
import { toggleButton } from './toggle-button.js';

interface School { id: string; name: string; level: string; grades?: number[] }
interface Material {
  releaseId: string; title: string; selected: boolean; assignmentId: string | null; continueOnly?: boolean; progress?: 'new' | 'in-progress' | 'done';
  textbook?: { id: string; title: string } | null; sectionName?: string | null; sectionOrder?: number | null;
}

const GRADE_MAX = (level: string): number => (level === 'elementary' ? 6 : 3);

const progOf = (m: Material): 'new' | 'in-progress' | 'done' => m.progress ?? (m.selected ? 'in-progress' : 'new');

/**
 * 이어하기 / 시작할 학습 / 완료 구역. 한 PDF에서 나온 구역은 서버가 기록한 교재(textbook)로 묶고 원문 구역 순서로 보인다.
 * 교재 정보가 없는 자료는 제목 그대로 따로 보인다(제목으로 합치지 않는다).
 */
export function materialSections(list: Material[], item: (m: Material) => HTMLElement, prefix: string, limit: number | null = null): HTMLElement[] {
  const group = (all: Material[], key: string): HTMLElement => {
    // 홈에서는 구역마다 앞의 몇 개만 보이고 나머지는 "내 학습"에서 본다(완료는 홈에 펼치지 않는다)
    const items = limit === null ? all : all.slice(0, limit);
    const books = new Map<string, Material[]>();
    const loose: Material[] = [];
    for (const m of items) {
      if (m.textbook != null) { const g = books.get(m.textbook.id) ?? []; g.push(m); books.set(m.textbook.id, g); } else loose.push(m);
    }
    const ul = h('ul', { class: 'alist', 'data-testid': key === 'new' ? `${prefix}-materials` : `${prefix}-materials-${key}` });
    for (const [id, ms] of [...books.entries()].sort((a, b) => (a[1][0]?.textbook?.title ?? '').localeCompare(b[1][0]?.textbook?.title ?? ''))) {
      ms.sort((a, b) => (a.sectionOrder ?? 0) - (b.sectionOrder ?? 0));
      ul.append(h('li', { 'data-testid': `book-${key}-${id}` }, h('strong', { class: 'at' }, ms[0]?.textbook?.title ?? ''), h('ul', { class: 'alist' }, ...ms.map(item))));
    }
    for (const m of loose) ul.append(item(m));
    return ul;
  };
  const section = (key: string, title: string, items: Material[]) => items.length === 0 ? null
    : h('section', { 'data-testid': `${prefix}-sec-${key}` }, h('h3', {}, `${title} (${items.length})`), group(items, key),
      limit !== null && items.length > limit ? h('p', { class: 'ev-note' }, `앞의 ${limit}개만 보여요. 나머지는 '내 학습'에서 볼 수 있어요.`) : null);
  return [section('continue', '이어하기', list.filter((m) => progOf(m) === 'in-progress')),
    section('new', '시작할 학습', list.filter((m) => progOf(m) === 'new')),
    section('done', '완료', list.filter((m) => progOf(m) === 'done'))].filter((s): s is HTMLElement => s !== null);
}

/** 학생 첫 화면(과제 목록) 위에 붙이는 내 자료 구역. 열람만으로는 시도를 만들지 않고, 시작하기를 누를 때 본인 기록이 생긴다. */
export function mountHomeMaterials(root: HTMLElement, t: Transport, onStart: (assignmentId: string) => void, opts: { limit?: number; onMore?: () => void; summary?: boolean } = {}): void {
  const gen = currentGen();
  const live = () => gen === currentGen() && root.isConnected;
  /** 학교급 경계에서 진학 학교를 학생이 고른다(학교 ID 선택, 임의 배정·자유 입력 없음). 미정이면 그대로 두고 지난 기록은 계속 볼 수 있다. */
  async function pendingSchool(level: string): Promise<void> {
    const s = await t.request('GET', '/schools');
    if (!live()) return;
    if (s.kind !== 'http' || s.status !== 200) { root.append(h('p', { class: 'notice', role: 'alert' }, '학교 목록을 불러오지 못했어요.'), h('button', { class: 'secondary', onclick: () => void load() }, '다시 불러오기')); return; }
    const schools = (s.body['schools'] as School[]).filter((x) => x.level === level);
    let schoolId = ''; let gradeNo = 0; let needGrade = false;
    const msg = h('p', { role: 'status', 'data-testid': 'next-school-msg' });
    const box = h('div', { role: 'group', 'aria-label': '진학할 학교', class: 'toggle-group', 'data-testid': 'next-school' });
    const gradeBox = h('div', { role: 'group', 'aria-label': '학년', class: 'toggle-group', 'data-testid': 'next-school-grade' });
    const draw = () => {
      clear(box); clear(gradeBox);
      for (const x of schools) box.append(toggleButton(x.name, x.id === schoolId, `next-school-${x.id}`, () => { schoolId = x.id; gradeNo = 0; draw(); }));
      const sel = schools.find((x) => x.id === schoolId);
      if (needGrade && sel !== undefined) for (const g of sel.grades ?? []) gradeBox.append(toggleButton(`${g}학년`, g === gradeNo, `next-grade-${g}`, () => { gradeNo = g; draw(); }));
    };
    draw();
    const ok = h('button', { class: 'primary', 'data-testid': 'next-school-save', onclick: async () => {
      if (schoolId === '') { msg.textContent = '학교를 골라 주세요.'; return; }
      if (needGrade && gradeNo < 1) { msg.textContent = '학년을 골라 주세요.'; return; }
      ok.disabled = true;
      const r = await t.request('POST', '/me/profile/next-school', { body: needGrade ? { schoolId, grade: gradeNo } : { schoolId } });
      if (!live()) return;
      ok.disabled = false;
      if (r.kind === 'http' && r.status === 200) { toast('진학한 학교를 저장했어요.'); void load(); return; }
      if (r.kind === 'http' && r.body['error'] === 'grade_required') { needGrade = true; msg.textContent = '지난 학년도가 있어 학년도 직접 골라 주세요.'; draw(); return; }
      msg.textContent = r.kind === 'network' ? '결과를 받지 못했어요. 같은 버튼을 다시 눌러 주세요.' : `저장하지 못했어요(${String(r.body['error'] ?? r.status)}).`;
    } }, '이 학교로 정하기');
    const later = h('button', { class: 'secondary', 'data-testid': 'next-school-later', onclick: () => { clear(root); root.append(h('p', { class: 'notice', 'data-testid': 'next-school-deferred' }, '아직 정해지지 않았어요. 정해지면 다시 안내해 드릴게요. 지난 학습 기록은 아래에서 계속 볼 수 있어요.')); } }, '아직 정해지지 않았어요');
    root.append(h('section', { 'data-testid': 'next-school-panel' }, h('h3', {}, '진학할 학교를 선택해 주세요'), h('p', {}, '새 학년이 되었어요. 학교를 정하면 그 학교의 자료가 자동으로 보여요.'), box, gradeBox, msg, ok, later));
  }

  async function load(): Promise<void> {
    const r = await t.request('GET', '/me/materials');
    if (!live()) return;
    clear(root);
    // 조회 실패는 빈 목록과 다르게 알리고 지난 목록은 지운다.
    if (r.kind !== 'http' || r.status !== 200) { root.append(h('p', { class: 'notice', role: 'alert', 'data-testid': 'home-materials-error' }, '학교 자료를 불러오지 못했어요.'), h('button', { class: 'secondary', 'data-testid': 'home-materials-retry', onclick: () => void load() }, '다시 불러오기')); return; }
    if (r.body['state'] === 'graduated') { root.append(h('p', { class: 'notice', 'data-testid': 'home-graduated' }, '졸업했어요. 지난 학습 기록과 결과는 아래 내 과제에서 계속 볼 수 있어요.')); return; }
    if (r.body['state'] === 'school-pending') { await pendingSchool(String(r.body['pendingLevel'])); return; }
    if (r.body['state'] !== 'ok') return;
    const list = r.body['materials'] as Material[];
    if (list.length === 0) return;
    const go =async (m: Material, btn: HTMLButtonElement): Promise<void> => {
      if (m.selected && m.assignmentId !== null) { onStart(m.assignmentId); return; }
      btn.disabled = true;
      const s = await t.request('PUT', `/me/materials/${m.releaseId}/selection`, { body: {} });
      if (!live()) return;
      if (s.kind === 'http' && s.status < 300 && typeof s.body['assignmentId'] === 'string') { onStart(s.body['assignmentId']); return; }
      await load();
      if (live()) root.prepend(h('p', { class: 'notice', role: 'alert' }, s.kind === 'network' ? '결과를 받지 못했어요. 같은 버튼을 다시 눌러 주세요.' : '이 자료는 지금 시작할 수 없어요.'));
    };
    const item = (m: Material): HTMLElement => {
      const p = progOf(m);
      const label = m.continueOnly === true || p === 'in-progress' ? '이어서 풀기' : p === 'done' ? '결과 보기' : '시작하기';
      return h('li', { 'data-testid': `home-material-${m.releaseId}` }, h('span', { class: 'at' }, m.textbook != null && m.sectionName != null ? m.sectionName : m.title), ' ',
        h('button', { class: 'primary', 'data-testid': `home-material-go-${m.releaseId}`, onclick: (e: Event) => void go(m, e.currentTarget as HTMLButtonElement) }, label),
        m.continueOnly === true ? h('span', { class: 'notice' }, ' 시작한 이전 버전이에요.') : null);
    };
    // 홈에서는 긴 자료 목록 대신 요약과 '내 학습' 링크만 둔다(전체 목록은 내 학습 화면).
    if (opts.summary === true) {
      const n = (k: 'new' | 'in-progress' | 'done') => list.filter((m) => progOf(m) === k).length;
      root.append(h('p', { 'data-testid': 'home-materials-summary' }, `내 학습 자료: 이어하기 ${n('in-progress')}개 · 시작 전 ${n('new')}개 · 완료 ${n('done')}개`),
        h('button', { class: 'secondary', type: 'button', 'data-testid': 'mat-all', onclick: opts.onMore }, '내 학습에서 보기'));
      return;
    }
    // 학교·학년에 맞춰 선생님이 제공한 자료 목록이다(친구 진도나 통계가 아니다)
    root.append(h('h3', {}, '우리 학교·학년 학습'), ...materialSections(list, item, 'home', opts.limit ?? null),
      ...(opts.onMore !== undefined ? [h('button', { class: 'secondary', type: 'button', 'data-testid': 'mat-all', onclick: opts.onMore }, '내 학습 전체 보기')] : []));
  }
  void load();
}

export function mountLearningSetup(root: HTMLElement, t: Transport, handlers: { onStart: (assignmentId: string) => void; onBack: () => void; forced?: boolean }): void {
  const gen = currentGen();
  const live = () => gen === currentGen() && root.isConnected;
  const msg = h('p', { role: 'status', 'data-testid': 'setup-msg' });
  const body = h('div', { 'data-testid': 'setup-body' });
  clear(root);
  // 강제 첫설정(프로필 없음)에서는 저장 전까지 목록으로 돌아가는 버튼을 두지 않는다. 저장한 뒤에는 일반 화면과 같다.
  const back = h('button', { class: 'secondary', 'data-testid': 'setup-back', onclick: handlers.onBack }, '과제 목록으로');
  if (handlers.forced === true) back.hidden = true;
  root.append(h('div', { class: 'lhead' }, h('h2', {}, '내 학교·학년과 자료'), back), msg, body);

  const say = (s: string) => { msg.textContent = s; };
  const retry = (label: string, fn: () => void) => h('button', { class: 'primary', 'data-testid': 'setup-retry', onclick: fn }, label);

  async function loadProfile(): Promise<void> {
    clear(body); say('');
    body.append(h('p', { class: 'loading' }, '불러오고 있어요'));
    const r = await t.request('GET', '/me/profile');
    if (!live()) return;
    clear(body);
    if (r.kind !== 'http' || r.status !== 200) { say('학교·학년을 불러오지 못했어요.'); body.append(retry('다시 불러오기', () => void loadProfile())); return; }
    if (r.body['registered'] !== true) { await showForm(null); return; }
    back.hidden = false;
    await loadMaterials(`${String(r.body['schoolName'])} ${String(r.body['grade'])}학년`);
  }

  async function showForm(current: { schoolId: string; grade: number } | null): Promise<void> {
    const s = await t.request('GET', '/schools');
    if (!live()) return;
    if (s.kind !== 'http' || s.status !== 200) { say('학교 목록을 불러오지 못했어요.'); body.append(retry('다시 불러오기', () => void loadProfile())); return; }
    const schools = s.body['schools'] as School[];
    // 학교 하나, 그 학교의 학년 하나를 버튼 토글로 고른다(자유 입력 없음). 학교를 바꾸면 학년 선택은 비운다.
    let schoolId = current?.schoolId ?? '';
    let gradeNo = current?.grade ?? 0;
    const schoolBox = h('div', { role: 'group', 'aria-label': '학교', class: 'toggle-group', 'data-testid': 'setup-school' });
    const gradeBox = h('div', { role: 'group', 'aria-label': '학년', class: 'toggle-group', 'data-testid': 'setup-grade' });
    const draw = () => {
      clear(schoolBox); clear(gradeBox);
      for (const x of schools) {
        schoolBox.append(toggleButton(x.name, x.id === schoolId, `setup-school-${x.id}`, () => { if (schoolId !== x.id) { schoolId = x.id; gradeNo = 0; } draw(); }));
      }
      const sel = schools.find((x) => x.id === schoolId);
      if (sel === undefined) { gradeBox.append(h('span', { class: 'hint' }, '먼저 학교를 골라 주세요.')); return; }
      for (const g of sel.grades ?? [1, 2, 3].filter((n) => n <= GRADE_MAX(sel.level))) {
        gradeBox.append(toggleButton(`${g}학년`, g === gradeNo, `setup-grade-${g}`, () => { gradeNo = g; draw(); }));
      }
    };
    draw();
    const save = h('button', { class: 'primary', type: 'submit', 'data-testid': 'setup-save' }, '저장하고 자료 보기');
    const form = h('form', {
      class: 'form-stack', 'data-testid': 'setup-form',
      onsubmit: async (e: Event) => {
        e.preventDefault();
        if (schoolId === '' || gradeNo < 1) { say('학교와 학년을 모두 골라 주세요.'); return; }
        save.disabled = true;
        const r = await t.request('PUT', '/me/profile', { body: { schoolId, grade: gradeNo } });
        if (!live()) return;
        save.disabled = false;
        if (r.kind === 'http' && (r.status === 200 || r.status === 201)) { toast('학교·학년을 저장했어요.'); await loadProfile(); return; }
        say(r.kind === 'network' ? '저장 결과를 받지 못했어요. 같은 버튼을 다시 눌러 주세요.' : `저장하지 못했어요(${String(r.body['error'] ?? r.status)}).`);
      },
    }, h('p', {}, '처음 한 번만 학교와 학년을 골라 주세요. 선생님이 정한 자료가 자동으로 보여요.'),
    h('div', { class: 'field' }, h('span', { class: 'label' }, '학교'), schoolBox),
    h('div', { class: 'field' }, h('span', { class: 'label' }, '학년'), gradeBox), save);
    body.append(form);
  }

  async function loadMaterials(profileText: string): Promise<void> {
    const r = await t.request('GET', '/me/materials');
    if (!live()) return;
    clear(body);
    const change = h('button', { class: 'secondary', 'data-testid': 'setup-change', onclick: () => void showFormAgain() }, '학교·학년 바꾸기');
    const head = h('p', { 'data-testid': 'setup-profile' }, `내 정보: ${profileText} `);
    head.append(change);
    // 조회 실패는 빈 목록과 다르게 표시하고, 지난 목록은 보여 주지 않는다.
    if (r.kind !== 'http' || r.status !== 200) { say('자료를 불러오지 못했어요.'); body.append(head, retry('다시 불러오기', () => void loadMaterials(profileText))); return; }
    say('');
    const state = r.body['state'];
    if (state === 'profile-required') { await showForm(null); return; }
    if (state === 'not-released') { body.append(head, h('p', { class: 'notice', 'data-testid': 'setup-not-released' }, '아직 자료를 열 수 없어요. 선생님 안내를 기다려 주세요.')); return; }
    if (state === 'school-pending' || state === 'graduated') {
      body.append(head, h('p', { class: 'notice', 'data-testid': 'setup-promotion' }, state === 'graduated' ? '졸업했어요. 지난 학습 기록은 내 과제에서 볼 수 있어요.' : '진학할 학교를 정하면 자료가 보여요. 과제 목록 첫 화면에서 학교를 골라 주세요.'));
      return;
    }
    const list = r.body['materials'] as Material[];
    if (list.length === 0) { body.append(head, h('p', { 'data-testid': 'setup-empty' }, '지금 내 학교·학년에 제공된 자료가 없어요. 선생님이 자료를 제공하면 여기에 나타나요.')); return; }
    // 학교·학년에 맞는 자료는 고르기 없이 바로 보인다. 시작할 학습 / 이어하기 / 완료로 나눠 보여 준다.
    // 목록을 여는 것만으로는 시도나 제출 기록을 만들지 않고, 시작하기를 누를 때 본인 기록이 생긴다.
    const item = (m: Material): HTMLElement => h('li', { 'data-testid': `material-${m.releaseId}` },
      h('span', { class: 'at' }, m.textbook != null && m.sectionName != null ? m.sectionName : m.title), ' ',
      m.continueOnly === true
        ? [h('button', { class: 'primary', 'data-testid': `material-start-${m.releaseId}`, onclick: () => { if (m.assignmentId !== null) handlers.onStart(m.assignmentId); } }, '이어서 풀기'),
          h('span', { class: 'notice', 'data-testid': `material-old-${m.releaseId}` }, ' 시작한 이전 버전이에요. 끝까지 이어서 풀 수 있어요.')]
        : m.selected
        ? h('button', { class: 'primary', 'data-testid': `material-start-${m.releaseId}`, onclick: () => { if (m.assignmentId !== null) handlers.onStart(m.assignmentId); } }, m.progress === 'done' ? '결과 보기' : m.progress === 'in-progress' ? '이어서 풀기' : '시작하기')
        : h('button', { class: 'primary', 'data-testid': `material-begin-${m.releaseId}`, onclick: (e: Event) => void begin(m, e.currentTarget as HTMLButtonElement, profileText) }, '시작하기'));
    body.append(head, ...materialSections(list, item, 'setup'));
  }

  /** 시작하기: 서버가 이 학생의 시작 조건(대상 반 소속)을 본인 기록으로 만들고 배정 ID를 돌려준다. 같은 요청은 멱등이다. */
  async function begin(m: Material, btn: HTMLButtonElement, profileText: string): Promise<void> {
    btn.disabled = true;
    const r = await t.request('PUT', `/me/materials/${m.releaseId}/selection`, { body: {} });
    if (!live()) return;
    if (r.kind === 'http' && r.status < 300 && typeof r.body['assignmentId'] === 'string') { handlers.onStart(r.body['assignmentId']); return; }
    const why = r.kind === 'network' ? '결과를 받지 못했어요. 같은 버튼을 다시 눌러 주세요.' : r.status === 404 ? '이 자료는 지금 시작할 수 없어요.' : `처리하지 못했어요(${String(r.body['error'] ?? r.status)}).`;
    await loadMaterials(profileText);
    if (live()) say(why);
  }

  async function showFormAgain(): Promise<void> {
    const p = await t.request('GET', '/me/profile');
    if (!live()) return;
    clear(body);
    if (p.kind !== 'http' || p.status !== 200) { say('학교·학년을 불러오지 못했어요.'); body.append(retry('다시 불러오기', () => void loadProfile())); return; }
    await showForm(p.body['registered'] === true ? { schoolId: String(p.body['schoolId']), grade: Number(p.body['grade']) } : null);
  }

  void loadProfile();
}
